import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useSelection } from '../context/SelectionContext';
import { useTable } from './TableMenuWrapper';
import { useTabletMode } from '../context/TabletModeContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  Coffee, Wine, UtensilsCrossed, Cake, Moon, Sun,
  ArrowLeft, ChevronRight, Plus, Star, Sparkles,
  ShoppingCart, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICONS = {
  coffee: Coffee,
  wine: Wine,
  utensils: UtensilsCrossed,
  cake: Cake,
  moon: Moon
};

// Images for each time slot - visual and appetizing
const SLOT_IMAGES = {
  manana: [
    'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&q=80', // Coffee
    'https://images.unsplash.com/photo-1484723091739-30a097e8f929?w=400&q=80', // Toast
    'https://images.unsplash.com/photo-1517433367423-c7e5b0f35086?w=400&q=80', // Croissant
  ],
  vermut: [
    'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&q=80', // Vermut glass
    'https://images.unsplash.com/photo-1626200419199-391ae4be7a41?w=400&q=80', // Olives tapas
    'https://images.unsplash.com/photo-1515443961218-a51367888e4b?w=400&q=80', // Charcuterie
  ],
  comida: [
    'https://images.unsplash.com/photo-1544025162-d76694265947?w=400&q=80', // Main dish
    'https://images.unsplash.com/photo-1515443961218-a51367888e4b?w=400&q=80', // Charcuterie
    'https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=400&q=80', // Wine cheese
  ],
  tarde: [
    'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&q=80', // Coffee
    'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=400&q=80', // Pastry
    'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=400&q=80', // Cake
  ],
  noche: [
    'https://images.unsplash.com/photo-1566633806327-68e152aaf26d?w=400&q=80', // Beer
    'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&q=80', // Wine
    'https://images.unsplash.com/photo-1626200419199-391ae4be7a41?w=400&q=80', // Tapas
  ]
};

// Background colors/gradients for each slot
const SLOT_STYLES = {
  manana: {
    gradient: 'from-amber-700 via-amber-600 to-yellow-600',
    accent: 'amber'
  },
  vermut: {
    gradient: 'from-red-800 via-rose-700 to-amber-600',
    accent: 'rose'
  },
  comida: {
    gradient: 'from-amber-700 via-amber-600 to-yellow-600',
    accent: 'orange'
  },
  tarde: {
    gradient: 'from-amber-800 via-amber-700 to-yellow-700',
    accent: 'amber'
  },
  noche: {
    gradient: 'from-stone-800 via-amber-900 to-stone-900',
    accent: 'amber'
  }
};


// ==================== CAPA A: POPUP VISUAL COMPLETO ====================
export const MomentoPopup = ({ onViewSelection, onSkip }) => {
  const { language } = useLanguage();
  const { addItem } = useSelection();
  const { isTablet } = useTabletMode();
  const [momento, setMomento] = useState(null);
  const [slotConfig, setSlotConfig] = useState(null);
  const [experience, setExperience] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMomento = async () => {
      try {
        const momentoRes = await fetch(`${API_URL}/api/products/momento/actual`);
        if (momentoRes.ok) {
          const momentoData = await momentoRes.json();
          setMomento(momentoData);
          
          // Only show popup if the slot is enabled and has a linked experience
          if (momentoData.experience && momentoData.popup_enabled !== false) {
            setExperience(momentoData.experience);
          } else {
            // No experience for this slot → skip popup entirely
            onSkip();
            return;
          }
          
          const configRes = await fetch(`${API_URL}/api/admin/momento/public/config`);
          if (configRes.ok) {
            const configData = await configRes.json();
            if (!configData.popup_enabled) { onSkip(); return; }
            const slot = configData.slots?.find(s => s.slot_id === momentoData.slot_id);
            if (slot) setSlotConfig(slot);
          }
        }
      } catch (error) {
        console.error('Error fetching momento:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMomento();
  }, [onSkip]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!momento) { onSkip(); return null; }

  const Icon = ICONS[momento.icon] || Coffee;
  const title = slotConfig?.title?.[language] || slotConfig?.title?.es || momento.title[language] || momento.title.es;
  const subtitle = slotConfig?.subtitle?.[language] || slotConfig?.subtitle?.es || momento.subtitle[language] || momento.subtitle.es;
  const popupImage = experience?.images?.[0]
    ? experience.images[0]
    : slotConfig?.popup_image 
      ? (slotConfig.popup_image.startsWith('/api') ? `${API_URL}${slotConfig.popup_image}` : slotConfig.popup_image)
      : null;
  const slotImages = (slotConfig?.images?.length > 0) ? slotConfig.images : (SLOT_IMAGES[momento.slot_id] || SLOT_IMAGES.comida);
  const slotStyle = SLOT_STYLES[momento.slot_id] || SLOT_STYLES.comida;

  // Helper to localize experience field
  const loc = (field) => field?.[language] || field?.es || '';

  // Handle adding experience pack to cart
  const handleAddExperience = () => {
    if (!experience) return;
    const name = experience.name?.[language] || experience.name?.es || 'Experiencia';
    addItem({
      id: experience.id,
      name: name,
      price: experience.price,
      image_url: experience.images?.[0] || experience.image_url,
      quantity: 1,
      options: []
    });
    toast.success(`+ ${name}`, { duration: 1200 });
    onSkip();
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-300 ${isTablet ? 'items-start pt-12 px-6' : 'items-center p-4'}`}
      data-testid="momento-popup"
    >
      <div
        className={`w-full overflow-hidden rounded-3xl shadow-2xl animate-in zoom-in-95 duration-300 ${isTablet ? 'max-w-2xl' : 'max-w-md'}`}
        style={{ backgroundColor: 'var(--sand-50)' }}
      >
        {/* Header with image or gradient — taller and 'contain' on tablet so the whole image is visible */}
        <div
          className={`relative ${popupImage ? '' : `bg-gradient-to-br ${slotStyle.gradient}`} overflow-hidden`}
          style={{
            height: isTablet ? '380px' : '224px',
            background: popupImage && isTablet ? '#1A1816' : undefined,
          }}
        >
          {popupImage ? (
            <>
              <img
                src={popupImage}
                alt=""
                loading="eager"
                decoding="async"
                className="absolute inset-0 w-full h-full"
                style={{ objectFit: isTablet ? 'contain' : 'cover', objectPosition: 'center' }}
              />
              {!isTablet && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20"></div>
              )}
            </>
          ) : (
            <>
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-black/10 rounded-full blur-2xl"></div>
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <div className="relative w-full h-full">
                  <div className="absolute top-0 left-0 w-40 h-40 rounded-2xl overflow-hidden shadow-xl transform -rotate-6 border-4 border-white/30">
                    <img src={slotImages[0]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute top-4 right-0 w-36 h-36 rounded-2xl overflow-hidden shadow-xl transform rotate-6 border-4 border-white/30">
                    <img src={slotImages[1]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-32 rounded-2xl overflow-hidden shadow-xl transform rotate-3 border-4 border-white/30">
                    <img src={slotImages[2]} alt="" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </>
          )}
          
          <button onClick={onSkip} className="absolute top-4 right-4 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors z-10" data-testid="skip-momento-btn">
            <X size={20} />
          </button>
          <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium flex items-center gap-2 z-10">
            <Icon size={16} />
            {momento.name[language] || momento.name.es}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles style={{ color: '#B78C3C' }} size={20} />
            <span className="text-sm font-medium uppercase tracking-wider" style={{ color: '#9E7530' }}>
              {experience 
                ? (language === 'es' ? 'Experiencia' : language === 'en' ? 'Experience' : language === 'cat' ? 'Experiencia' : 'Expérience')
                : (language === 'es' ? 'Nuestra selección' : language === 'en' ? 'Our selection' : language === 'cat' ? 'La nostra selecció' : 'Notre sélection')
              }
            </span>
          </div>
          
          {experience ? (
            <>
              {/* Experience pack mode */}
              <h2 className="text-2xl font-serif mb-1" style={{ color: 'var(--stone-800)' }}>
                {loc(experience.name)}
              </h2>
              {experience.experience_copy && loc(experience.experience_copy) && (
                <p style={{ color: 'var(--stone-500)' }} className="mb-3 text-sm">{loc(experience.experience_copy)}</p>
              )}
              
              {/* Items included */}
              {experience.experience_items?.length > 0 && (
                <div className="text-left mb-4 space-y-1.5">
                  {experience.experience_items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm" style={{ color: 'var(--stone-600)' }}>
                      <span style={{ color: '#B78C3C' }}>&#10003;</span>
                      {loc(item)}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Target people badge */}
              {experience.experience_target_people && experience.experience_target_people !== '1' && (
                <p className="text-xs mb-3" style={{ color: 'var(--stone-400)' }}>
                  {language === 'es' ? `Para ${experience.experience_target_people} personas` : `For ${experience.experience_target_people} people`}
                </p>
              )}
              
              {/* Price + Add button */}
              <Button
                onClick={handleAddExperience}
                className={`w-full h-14 text-lg font-medium bg-gradient-to-r ${slotStyle.gradient} hover:opacity-90 text-white rounded-xl shadow-lg`}
                data-testid="add-experience-btn"
              >
                {loc(experience.name)} — {experience.price?.toFixed(2)}EUR
              </Button>
            </>
          ) : (
            <>
              {/* Original curated selection mode */}
              <h2 className="text-2xl font-serif mb-2" style={{ color: 'var(--stone-800)' }}>{title}</h2>
              <p style={{ color: 'var(--stone-500)' }} className="mb-6">{subtitle}</p>
              
              <Button
                onClick={onViewSelection}
                className={`w-full h-14 text-lg font-medium bg-gradient-to-r ${slotStyle.gradient} hover:opacity-90 text-white rounded-xl shadow-lg`}
                data-testid="ver-seleccion-popup-btn"
              >
                {language === 'es' && 'Ver selección'}
                {language === 'en' && 'View selection'}
                {language === 'cat' && 'Veure selecció'}
                {language === 'fr' && 'Voir la sélection'}
                <ChevronRight size={20} className="ml-2" />
              </Button>
            </>
          )}
          
          <button onClick={onSkip} className="mt-4 text-sm transition-colors" style={{ color: 'var(--stone-400)' }} data-testid="ver-carta-btn">
            {language === 'es' && 'Ver carta completa'}
            {language === 'en' && 'View full menu'}
            {language === 'cat' && 'Veure carta completa'}
            {language === 'fr' && 'Voir le menu complet'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ==================== CAPA A: BANNER (versión mini para después) ====================
export const MomentoBanner = ({ onViewExperience, onDismiss }) => {
  const { language, localize } = useLanguage();
  const { isDark } = useTheme();
  const [momento, setMomento] = useState(null);
  const [experience, setExperience] = useState(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const fetchMomento = async () => {
      try {
        const response = await fetch(`${API_URL}/api/products/momento/actual`);
        if (response.ok) {
          const data = await response.json();
          setMomento(data);
          if (data.experience && data.banner_enabled !== false) {
            setExperience(data.experience);
          }
        }
      } catch (error) {
        console.error('Error fetching momento:', error);
      }
    };
    fetchMomento();
  }, []);

  // Only show banner if there's an active experience
  if (!momento || !visible || !experience) return null;

  const Icon = ICONS[momento.icon] || Coffee;
  const slotStyle = SLOT_STYLES[momento.slot_id] || SLOT_STYLES.comida;
  const expName = localize(experience.name);
  const expCopy = experience.experience_copy ? localize(experience.experience_copy) : null;

  return (
    <div 
      className="mx-4 mb-3 animate-fade-up"
      data-testid="momento-banner"
    >
      <div 
        className={`relative overflow-hidden rounded-xl py-2.5 px-3 bg-gradient-to-r ${slotStyle.gradient} text-white`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
            <Icon className="text-white" size={16} />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{expName} — {experience.price?.toFixed(2)}€</p>
            {expCopy && <p className="text-[11px] text-white/75 truncate">{expCopy}</p>}
          </div>
          
          <button
            onClick={(e) => { e.stopPropagation(); onViewExperience?.(experience); }}
            className="flex items-center gap-1 px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs font-medium transition-colors flex-shrink-0"
            data-testid="ver-seleccion-btn"
          >
            {language === 'es' ? 'Ver' : language === 'cat' ? 'Veure' : language === 'fr' ? 'Voir' : 'View'}
            <ChevronRight size={12} />
          </button>
          <button
            onClick={() => { setVisible(false); onDismiss?.(); }}
            className="p-1 text-white/50 hover:text-white transition-colors flex-shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};


// ==================== CAPA B: SUBCARTA CURADA CON FOTOS ====================
export const MomentoCurado = ({ onBack }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { tableNumber } = useTable();
  const { addItem, items } = useSelection();
  
  const [loading, setLoading] = useState(true);
  const [curadoData, setCuradoData] = useState(null);

  const getItemQuantity = (productId) => {
    const item = items.find(i => i.id === productId);
    return item ? item.quantity : 0;
  };

  useEffect(() => {
    const fetchCurado = async () => {
      try {
        const response = await fetch(`${API_URL}/api/products/momento/curado?lang=${language}`);
        if (response.ok) {
          const data = await response.json();
          setCuradoData(data);
        }
      } catch (error) {
        console.error('Error fetching curado:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCurado();
  }, [language]);

  const handleAddProduct = (product) => {
    const productName = getProductName(product);
    addItem({
      id: product.id,
      name: productName,
      price: product.price,
      image_url: product.image_url || product.images?.[0],
      quantity: 1,
      options: []
    });
    toast.success(`+ ${productName}`, { duration: 800 });
  };

  const getProductName = (product) => {
    if (!product?.name) return 'Producto';
    if (typeof product.name === 'string') return product.name;
    return product.name[language] || product.name.es || 'Producto';
  };

  const getProductImage = (product) => {
    return product.image_url || product.images?.[0] || 
      'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&q=80';
  };

  const slotStyle = curadoData ? (SLOT_STYLES[curadoData.slot_id] || SLOT_STYLES.comida) : SLOT_STYLES.comida;
  const Icon = curadoData ? (ICONS[curadoData.slot_id === 'vermut' ? 'wine' : curadoData.slot_id === 'manana' ? 'coffee' : curadoData.slot_id === 'comida' ? 'utensils' : curadoData.slot_id === 'tarde' ? 'cake' : 'moon'] || Coffee) : Coffee;

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : 'var(--sand-50)' }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!curadoData) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6" style={{ backgroundColor: isDark ? '#1a1a1a' : 'var(--sand-50)' }}>
        <p className={isDark ? 'text-gray-400' : 'text-stone-500'}>No hay productos disponibles</p>
        <Button onClick={onBack} className="mt-4">Volver</Button>
      </div>
    );
  }

  const title = curadoData.slot_title[language] || curadoData.slot_title.es;
  const subtitle = curadoData.slot_subtitle[language] || curadoData.slot_subtitle.es;
  const cartCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div 
      className="min-h-dvh transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#1a1a1a' : 'var(--sand-50)' }}
      data-testid="momento-curado-screen"
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-20 px-4 py-3 backdrop-blur-md transition-colors duration-300" 
        style={{ 
          borderBottom: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(45, 42, 38, 0.08)',
          backgroundColor: isDark ? 'rgba(26,26,26,0.95)' : 'rgba(250,248,245,0.95)'
        }}
      >
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className={`p-2 -ml-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-stone-100'}`}
              data-testid="back-to-menu-btn"
            >
              <ArrowLeft className={isDark ? 'text-gray-300' : 'text-stone-600'} size={20} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-stone-800'}`}>{title}</span>
                {tableNumber && (
                  <Badge className="bg-[#B78C3C] text-white text-xs px-1.5 py-0">#{tableNumber}</Badge>
                )}
              </div>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-stone-500'}`}>
                {curadoData.total_products} productos seleccionados
              </p>
            </div>
          </div>
          
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full transition-all ${isDark ? 'bg-slate-700 text-yellow-400' : 'bg-stone-200 text-stone-600'}`}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      {/* Hero section with gradient */}
      <div className={`bg-gradient-to-br ${slotStyle.gradient} px-4 py-8 text-center text-white`}>
        <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/20 flex items-center justify-center">
          <Icon className="text-white" size={32} />
        </div>
        <h1 className="text-2xl font-serif mb-1">{title}</h1>
        <p className="text-white/80">{subtitle}</p>
      </div>

      {/* Content with product cards WITH IMAGES */}
      <div className="px-4 pb-32 pt-6 max-w-2xl mx-auto space-y-6">
        {/* Block 1: Best Sellers */}
        <ProductBlockWithImages 
          title={curadoData.blocks.best_sellers.title[language] || curadoData.blocks.best_sellers.title.es}
          products={curadoData.blocks.best_sellers.products}
          onAddProduct={handleAddProduct}
          getProductName={getProductName}
          getProductImage={getProductImage}
          getItemQuantity={getItemQuantity}
          isDark={isDark}
          icon={<Star size={18} className="text-amber-500" />}
        />

        {/* Block 2: Maridajes */}
        {curadoData.blocks.maridajes.products.length > 0 && (
          <ProductBlockWithImages 
            title={curadoData.blocks.maridajes.title[language] || curadoData.blocks.maridajes.title.es}
            products={curadoData.blocks.maridajes.products}
            onAddProduct={handleAddProduct}
            getProductName={getProductName}
            getProductImage={getProductImage}
            getItemQuantity={getItemQuantity}
            isDark={isDark}
            icon={<Wine size={18} style={{ color: '#B78C3C' }} />}
          />
        )}

        {/* Block 3: Premium */}
        {curadoData.blocks.premium.products.length > 0 && (
          <ProductBlockWithImages 
            title={curadoData.blocks.premium.title[language] || curadoData.blocks.premium.title.es}
            products={curadoData.blocks.premium.products}
            onAddProduct={handleAddProduct}
            getProductName={getProductName}
            getProductImage={getProductImage}
            getItemQuantity={getItemQuantity}
            isDark={isDark}
            icon={<Sparkles size={18} className="text-amber-400" />}
            isPremium
            gradient={slotStyle.gradient}
          />
        )}

        {/* View full menu button */}
        <div className="pt-4">
          <Button
            variant="outline"
            onClick={onBack}
            className={`w-full h-12 ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-stone-300 text-stone-600 hover:bg-stone-100'}`}
            data-testid="ver-carta-completa-btn"
          >
            {language === 'es' && 'Ver carta completa'}
            {language === 'en' && 'View full menu'}
            {language === 'cat' && 'Veure carta completa'}
            {language === 'fr' && 'Voir le menu complet'}
          </Button>
        </div>
      </div>

      {/* Floating cart */}
      {cartCount > 0 && (
        <div className="fixed bottom-6 left-4 right-4 z-30 max-w-2xl mx-auto">
          <div className={`rounded-2xl p-4 shadow-lg ${isDark ? 'bg-stone-800 border border-stone-700' : 'bg-white border border-stone-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#B78C3C] rounded-xl flex items-center justify-center">
                  <ShoppingCart className="text-white" size={20} />
                </div>
                <div>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-stone-800'}`}>
                    {cartCount} {cartCount === 1 ? 'producto' : 'productos'}
                  </p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-stone-500'}`}>
                    {items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)}€
                  </p>
                </div>
              </div>
              <Button onClick={onBack} className="bg-[#B78C3C] hover:bg-[#9E7530] text-white">
                {language === 'es' ? 'Continuar' : 'Continue'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== PRODUCT BLOCK WITH IMAGES ====================
const ProductBlockWithImages = ({ title, products, onAddProduct, getProductName, getProductImage, getItemQuantity, isDark, icon, isPremium, gradient }) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className={`font-medium ${isDark ? 'text-white' : 'text-stone-800'}`}>{title}</h2>
      </div>
      
      <div className="grid grid-cols-1 gap-3">
        {products.map((product) => {
          const quantity = getItemQuantity(product.id);
          const name = getProductName(product);
          const image = getProductImage(product);
          
          return (
            <div 
              key={product.id}
              className={`relative rounded-2xl overflow-hidden transition-all ${
                isPremium 
                  ? `bg-gradient-to-r ${gradient || 'from-amber-500 to-orange-500'} p-[2px]` 
                  : ''
              } ${quantity > 0 ? 'ring-2 ring-[#B78C3C]' : ''}`}
            >
              <div className={`flex items-center gap-4 p-3 rounded-2xl ${
                isPremium 
                  ? isDark ? 'bg-stone-900' : 'bg-white'
                  : isDark ? 'bg-stone-800/50 border border-stone-700/50' : 'bg-white border border-stone-200'
              }`}>
                {/* Product image */}
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                  <img 
                    src={image} 
                    alt={name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&q=80'; }}
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      {isPremium && (
                        <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs mb-1">
                          Premium
                        </Badge>
                      )}
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-stone-800'}`}>
                        {name}
                      </p>
                      <p className={`text-lg font-bold mt-1 ${isPremium ? 'text-amber-500' : 'text-[#D4AF37]'}`}>
                        {product.price?.toFixed(2)}€
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {quantity > 0 && (
                        <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-stone-800'}`}>
                          {quantity}
                        </span>
                      )}
                      <button
                        onClick={() => onAddProduct(product)}
                        className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                          quantity > 0 
                            ? 'bg-[#B78C3C] text-white' 
                            : isDark 
                              ? 'bg-stone-700 text-white hover:bg-stone-600' 
                              : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                        }`}
                        data-testid={`add-product-${product.id}`}
                      >
                        <Plus size={24} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MomentoCurado;
